import { useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Compass, MessageCircle, Sparkles, Clock, BookHeart, Sparkle } from 'lucide-react';
import './TabBar.css';

/* iOS Safari 在 standalone（添加到主屏幕）模式下，对 <a href> 会强行推到浏览器
   即使 JS preventDefault 也来不及。用 <button> + navigate() 完全绕过这个 bug。 */

/* 2026-07-04 铃拍板：塔罗撤出导航（首页卡片直达），星洲移到正中。
   2026-07-19 铃改主意：星洲的「主星」突出效果（大图标+常亮微光）看着突兀，
   撤掉 —— 五个按钮同等待遇（nexus 样式已随之删除）。 */
const TABS = [
  { to: '/chat', label: '聊天', icon: MessageCircle },
  { to: '/memory', label: '记忆', icon: Sparkles },
  { to: '/home', label: '星洲', icon: Compass },
  { to: '/timeline', label: '时间', icon: Clock },
  { to: '/diary', label: '日记', icon: BookHeart },
  { to: '/ying', label: '萤', icon: Sparkle },
];

// 页面级导航栏配色覆盖：这些路由强制导航栏走深紫（不跟随全局浅色主题），
// 让导航栏和页面自带的深色设计统一。其余页面留空 = 跟随全局 light/dark。
const NAV_THEME = { '/tarot': 'dark' };

export default function TabBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  /* 「有时要点好几下」修复（2026-07-19 铃报）：内容还在惯性滚动时，iOS 把
     第一下点按用来刹车、click 事件被吞掉。改成在 pointerup（手指抬起）时就
     导航——刹车那一下照样触发。pointerdown 记起点，抬起时位移超过阈值视为
     拖动/滑动不导航。click 保留给键盘和不支持 pointer 的环境当兜底，
     用 500ms 去重防止和 pointerup 各跳一次。 */
  const pressRef = useRef(null);
  const lastNavRef = useRef({ to: '', t: 0 });
  function go(to) {
    const now = Date.now();
    if (lastNavRef.current.to === to && now - lastNavRef.current.t < 500) return;
    lastNavRef.current = { to, t: now };
    navigate(to);
  }
  const navKey = Object.keys(NAV_THEME).find(
    (p) => pathname === p || pathname.startsWith(p + '/'),
  );
  // 滑动选中圆片（2026-07-09 液态玻璃改版）：算出激活 tab 的序号喂给
  // CSS 变量 --ti，.tab-ind 按整格平移过去；路由不在导航里时隐身。
  const activeIndex = TABS.findIndex(
    ({ to }) => pathname === to || pathname.startsWith(to + '/'),
  );

  return (
    <nav
      className="tabbar"
      aria-label="主导航"
      data-nav={navKey ? NAV_THEME[navKey] : undefined}
    >
      <div className="tabbar-inner" style={{ '--ti': Math.max(activeIndex, 0) }}>
        <span
          className="tab-ind"
          aria-hidden="true"
          data-off={activeIndex < 0 ? '' : undefined}
        />
        {TABS.map(({ to, label, icon: Icon }) => {
          const isActive = pathname === to || pathname.startsWith(to + '/');
          return (
            <button
              key={to}
              type="button"
              className={'tab' + (isActive ? ' on' : '')}
              onPointerDown={(e) => {
                pressRef.current = { x: e.clientX, y: e.clientY };
              }}
              onPointerUp={(e) => {
                const p = pressRef.current;
                pressRef.current = null;
                if (!p) return;
                if (Math.abs(e.clientX - p.x) > 12 || Math.abs(e.clientY - p.y) > 12) return;
                go(to);
              }}
              onClick={() => go(to)}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon size={18} strokeWidth={1.4} className="tab-ic" />
              <span className="tab-lb">{label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
